import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, useMapEvents } from 'react-leaflet';
import type { LatLngTuple } from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';

import DogWalksLayer from './DogWalksLayer';

const dogKeywords = [
  'dog', 'dogs', 'puppy', 'puppies', 'canine',
  'pet-friendly', 'pets allowed', 'no dogs',
  'leash', 'off-leash', 'dog-friendly'
];

function mentionsDogs(text: string = ''): boolean {
  const lower = text.toLowerCase();
  return dogKeywords.some(keyword => lower.includes(keyword));
}

type DoggyReviewEntry = ReviewEntry & { siteID: number };

const customIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  shadowSize: [41, 41]
});


const UK_CENTER: LatLngTuple = [54.5, -3]; // Center of UK

export interface Campsite {
  ID: string;
  Name: string;
  latitude: number;
  longitude: number;
  rT?: number;
  facilities?: string;
  reviews?: ReviewEntry[];
  description: string;
  lt: number;
  lg: number;
  town: string;
  dis: string;
  thumb: string;
}

export interface ReviewEntry {
  user: {
    id: number;
    name: string;
    unitDescription: string;
    unitIcon: string;
    avatar: string | null;
    countryID: number | null;
  };
  review: {
    siteID: string,
    id: number;
    text: string;
    date: string;
    updated: string;
    unitDescription: string;
    unitTypeID: number;
    unitIcon: string;
    unitLength: string;
    rating: {
      price: number;
      view: number;
      location: number;
      facilities: number;
      score: number;
    };
  };
}


type POI = {
  name: string;
  description: string;
  latitude: number;
  longitude: number;
};


interface MapProps {
  onMapClick: (coords: { latitude: number; longitude: number }) => void;
  pois: POI[];
  onMarkerClick: (index: number) => void;
  markers: Campsite[];
  mapCenter: { lat: number; lng: number } | null;
  sites: Campsite[]; // or whatever your site array type is
  selectedSite: Campsite | null;
  onSiteSelect: (site: Campsite) => void;
  setMapCenter: (coords: { lat: number; lng: number }) => void;
  fetchNearbySites: (lat: number, lng: number) => void;
}

const MapClickHandler: React.FC<{ onMapClick: (coords: { latitude: number; longitude: number }) => void }> = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      onMapClick({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    }
  });
  return null;
};

const MapZoomHandler: React.FC<{ center: { lat: number; lng: number } | null }> = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], 13); // Adjust zoom level as needed
    }
  }, [center, map]);

  return null;
};

const renderStars = (rating: number) => {
  const rating5 = rating / 2; // Convert 10-point scale to 5-point scale
  const fullStars = Math.floor(rating5);
  const halfStar = rating5 % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

  return (
    <span style={{ color: '#f5a623', fontSize: '16px' }}>
      {'★'.repeat(fullStars)}
      {halfStar && '½'}
      {'☆'.repeat(emptyStars)}
    </span>
  );
};

const fetchReviews = async (markerID: string, offset: number = 0): Promise<ReviewEntry[]> => {
  const url = 'https://corsproxy.io/?https://www.searchforsites.co.uk/pdo/getReviews.php';
  const payload = new URLSearchParams({ markerID, offset: offset.toString() });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: payload.toString()
    });

    const data = await response.json();

    if (data?.reviews) {
      console.log("Review data 1:", data.reviews);
    }

    return data.reviews || [];
  } catch (err) {
    console.error('Review fetch failed:', err);
    return [];
  }
};






const Map: React.FC<MapProps> = ({ onMapClick, pois, onMarkerClick, markers, mapCenter, setMapCenter,
  sites, selectedSite, onSiteSelect, fetchNearbySites }) => {
  const [reviewPopupSite, setReviewPopupSite] = useState<Campsite | null>(null);
  const [reviewOffset, setReviewOffset] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showDogOnly, setShowDogOnly] = useState(false);
  const [newReview, setNewReview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [doggyReviews, setDoggyReviews] = useState<DoggyReviewEntry[]>([]);
  const [isAddingReview, setIsAddingReview] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [showAreaModal, setShowAreaModal] = useState(true); // true on initial load
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<{ name: string; lat: number; lng: number; }[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);



  



  const handleOpenReviewModal = (site: Campsite) => {
    console.log('Opening modal for site:', site);
    setReviewPopupSite(site);
    setIsAddingReview(true);
    setShowReviewModal(true);
  };

  const handleSearchLocation = async () => {
    if (!searchQuery.trim()) return;

    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`);
      const results = await response.json();

      if (results.length > 0) {
        const { lat, lon } = results[0];
        setShowAreaModal(false); // close modal
        onMapClick({ latitude: parseFloat(lat), longitude: parseFloat(lon) }); // zoom map
      } else {
        alert('Location not found. Try a different name.');
      }
    } catch (err) {
      console.error('Search failed:', err);
      alert('Error searching for location.');
    }
  };




  useEffect(() => {
    fetch('/api/doggle-reviews')
      .then(res => res.json())
      .then(data => setDoggyReviews(data))
      .catch(err => console.error('Failed to load doggy reviews:', err));
  }, []);

  useEffect(() => {
    const fetchResults = async () => {
      if (searchTerm.length >= 3) {
        try {
          const proxyUrl = "https://corsproxy.io/?";
          const apiUrl = "https://www.searchforsites.co.uk/pdo/getPlaceNames.php";
          const formData = new URLSearchParams({
            limt: '15',
            term: searchTerm
          });
          const response = await fetch(proxyUrl + apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            },
            body: formData.toString()
          });
          const data = await response.json();
          const placesObj = data.tblAllPlaces || {};
          const places = Object.values(placesObj)
            .filter((place: any) => place.c === "gb")
            .map((place: any) => {
              const [lat, lng] = (place.l || "0,0").split(',').map(Number);
              return { name: place.t, lat, lng };
            });
          setSearchResults(places);
          setShowDropdown(true);
        } catch (error) {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    };
    fetchResults();
  }, [searchTerm]);




  const reviewsToShow = showDogOnly
    ? reviewPopupSite?.reviews?.filter(entry => mentionsDogs(entry.review.text))
    : reviewPopupSite?.reviews;


  const handleCloseReviewModal = () => {
    setShowReviewModal(false);
    setIsAddingReview(false);
    setReviewSubmitted(false);
    setReviewPopupSite(null);
  };

  const handlePlaceSelect = (place: { name: string; lat: number; lng: number }) => {
    setSearchTerm(place.name);
    setShowAreaModal(false);
    onMapClick({ latitude: place.lat, longitude: place.lng });
    setMapCenter({ lat: place.lat, lng: place.lng });
    fetchNearbySites(place.lat, place.lng); 
            
  };


  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reviewPopupSite?.ID || !newReview.trim()) return;

    setIsSubmitting(true);

    const payload = {
      siteID: reviewPopupSite.ID,
      text: newReview,
      user: { name: 'Anonymous' },
      review: {
        date: new Date().toISOString().split('T')[0],
        text: newReview,
        rating: { score: 10 } // or let user choose later
      }
    };

    const newReviewEntry: ReviewEntry = {
      user: {
        id: Date.now(),
        name: 'Anonymous',
        unitDescription: 'Guest',
        unitIcon: '🐾',
        avatar: null,
        countryID: null
      },
      review: {
        siteID: reviewPopupSite.ID,
        id: Date.now(),
        text: newReview,
        date: new Date().toISOString().split('T')[0],
        updated: new Date().toISOString(),
        unitDescription: 'Guest',
        unitTypeID: 0,
        unitIcon: '🐾',
        unitLength: 'N/A',
        rating: {
          price: 10,
          view: 10,
          location: 10,
          facilities: 10,
          score: 10
        }
      }
    };





    try {
      // Replace with your actual POST endpoint
      await fetch('/api/doggle-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // Update local state
      const updatedSite: Campsite = {
        ...reviewPopupSite,
        reviews: [...(reviewPopupSite.reviews || []), newReviewEntry]
      };

      setNewReview('');
      setReviewSubmitted(true);
    } catch (err) {
      console.error('Failed to submit review:', err);
    } finally {
      setIsSubmitting(false);
    }
  }


  const dogReviews = reviewPopupSite?.reviews
    ? reviewPopupSite.reviews.filter(entry => mentionsDogs(entry.review.text))
    : [];

  const doggyReviewsForSite = doggyReviews.filter(
    entry => entry.siteID === Number(reviewPopupSite?.ID)
  );

  const filteredSearchforSiteReviews = showDogOnly
    ? reviewPopupSite?.reviews?.filter(entry => mentionsDogs(entry.review.text))
    : reviewPopupSite?.reviews || [];

  const allReviewsToShow = [
    ...doggyReviewsForSite,
    ...(filteredSearchforSiteReviews || [])
  ];



  if (reviewPopupSite?.reviews) {
    console.log("Review data:", reviewPopupSite.reviews);
  } else { console.log }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{        
        padding: '12px 24px',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #ddd',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{ margin: 0 }}>🐾 Doggle Campsite Explorer</h1>
        <button
          onClick={() => setShowAreaModal(true)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#2196f3',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'transform 0.2s ease-in-out'
          }}
          onMouseDown={e => e.currentTarget.style.transform = 'scale(0.95)'}
          onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          🔍 Select Location
        </button>
      </div>

      <MapContainer center={UK_CENTER} zoom={6} className="custom-map">
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        <MapClickHandler onMapClick={onMapClick} />
        <MapZoomHandler center={mapCenter} />
        <DogWalksLayer />
        {pois.map((poi, idx) => (
          <Marker
            key={idx}
            position={[poi.latitude, poi.longitude]}
            eventHandlers={{
              click: () => onMarkerClick(idx),
            }}
          >
            <Popup>
              <strong>{poi.name}</strong>
              <br />
              {poi.description}
            </Popup>
          </Marker>
        ))}

        <MarkerClusterGroup
          iconCreateFunction={(cluster: any) => {
            const count = cluster.getChildCount();
            return L.divIcon({
              html: `<div class="custom-cluster">🐾 ${count}</div>`,
              className: 'custom-cluster-icon',
              iconSize: L.point(40, 40, true)
            });
          }}
        >
          {markers.map((site) => (
            <Marker
              key={site.ID || `${site.lt}-${site.lg}`}
              position={[site.lt, site.lg]}
              icon={customIcon}
              eventHandlers={{
                click: async () => {
                  if (site.ID) {
                    const reviews = await fetchReviews(site.ID);
                    const enrichedSite = { ...site, reviews };
                    onSiteSelect({ ...site, reviews });
                  }
                }
              }}
            >
              <Popup>
                <div style={{ maxWidth: '250px', fontFamily: 'sans-serif', lineHeight: '1.4' }}>
                  <strong>{site.Name}</strong><br />
                  <em>{site.town}, {site.dis}</em><br />

                  <button onClick={() => handleOpenReviewModal(site)}>
                    🐾 Add Doggy Review
                  </button>

                  {/* Star Rating */}
                  {site.rT && (
                    <div
                      style={{ marginTop: '6px', color: '#f5a623', cursor: 'pointer', fontSize: '1.1em' }}
                      onClick={async () => {
                        if (!site.reviews && site.ID) {
                          const reviews = await fetchReviews(site.ID);
                          setReviewPopupSite({ ...site, reviews });
                        } else {
                          setReviewPopupSite(site); // already has reviews
                        }
                      }}
                      title="Click to view reviews"
                    >
                      {renderStars(site.rT)} &nbsp;



                    </div>
                  )}


                  {/* Thumbnail Image */}
                  {site.thumb && (
                    <img
                      src={`https://www.searchforsites.co.uk/images/thumbs/${site.thumb}`}
                      alt={site.Name}
                      style={{ width: '100%', borderRadius: '8px' }}
                    />
                  )}

                  {/* Description or other details */}
                  {site.description && (
                    <p style={{ marginTop: '6px' }}>{site.description}</p>
                  )}



                  {/* Optional: Facilities list */}
                  {/* typeof site.facilities === 'string' && site.facilities.trim() !== '' && (
              <ul style={{ paddingLeft: '16px', marginTop: '6px' }}>
                {site.facilities.split(',').map((f, i) => (
                  <li key={i}>{f.trim()}</li>
                ))}
              </ul>
            ) */}



                </div>
              </Popup>

            </Marker>
          ))}
        </MarkerClusterGroup>

      </MapContainer>
      {!isAddingReview && reviewPopupSite && (
        <div
          style={{
            position: 'absolute',
            top: '20%',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
            zIndex: 1000,
            maxWidth: '400px'
          }}
        >

          <h3>Reviews for {reviewPopupSite.Name}</h3>

          <button onClick={() => setShowDogOnly(prev => !prev)}>
            {showDogOnly ? 'Show All Reviews' : '🐶 Show Dog Mentions Only'}
          </button>

          <button onClick={() => setReviewPopupSite(null)} style={{ float: 'right' }}>✖</button>
          <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '8px', marginBottom: '12px' }}>
            <ul style={{ paddingLeft: '16px' }}>
              {allReviewsToShow?.map((entry, i) => (
                <li key={i} style={{ marginBottom: '10px' }}>
                  <strong>{entry.user.name}</strong> <em>({entry.review.date})</em><br />
                  {entry.review.text}<br />
                  <span style={{ fontSize: '0.85em', color: '#666' }}>
                    Score: {entry.review.rating.score}/10
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {reviewPopupSite && (
            <div>
              <button
                onClick={async () => {
                  setIsLoadingMore(true);
                  const moreReviews = await fetchReviews(reviewPopupSite.ID, reviewOffset + 10); // or whatever your page size is
                  const updatedSite = {
                    ...reviewPopupSite,
                    reviews: [...(reviewPopupSite.reviews || []), ...moreReviews]
                  };
                  setReviewPopupSite(updatedSite);
                  setReviewOffset(prev => prev + 10);
                  setIsLoadingMore(false);
                }}
                disabled={isLoadingMore}
                style={{
                  marginTop: '8px',
                  padding: '6px 12px',
                  background: '#eee',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9em'
                }}
              >

                {isLoadingMore ? 'Loading...' : 'Load more reviews'}
              </button>

            </div>

          )}




        </div>
      )}

      {showReviewModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowReviewModal(false)} // close on backdrop click
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '10px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}
            onClick={e => e.stopPropagation()} // prevent closing when clicking inside
          >

            <h3>🐶 Share Your Doggy Experience</h3>
            {reviewSubmitted ? (
              <div style={{ textAlign: 'center' }}>
                <h3>🐾 Thank you for your review!</h3>
                <p>We appreciate your feedback. Your review will appear once approved.</p>
                <button onClick={handleCloseReviewModal}>Close</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <textarea
                  value={newReview}
                  onChange={e => setNewReview(e.target.value)}
                  placeholder="Tell us how your pup enjoyed this site..."
                  rows={5}
                  style={{ width: '100%', marginBottom: '12px' }}
                />
                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Review'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReviewModal(false)}
                  style={{ marginLeft: '10px' }}
                >
                  Cancel
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {showAreaModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            animation: 'fadeIn 0.4s ease-in-out'
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              textAlign: 'center',
              animation: 'scaleIn 0.3s ease-in-out'
            }}
          >
            <h2>📍 Search for a Location</h2>
            <div style={{ position: 'relative', width: '80%' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search Place Name"
              style={{ width: '80%', padding: '10px', marginBottom: '12px' }}
            />

            
  {searchTerm && (
    <button
      onClick={() => setSearchTerm('')}
      style={{
        position: 'absolute',
        right: '25px',
        top: '40%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        fontSize: '16px',
        cursor: 'pointer'
      }}
      aria-label="Clear search"
    >
      ✖️
    </button>
  )}
</div>

            {showDropdown && searchResults.length > 0 && (
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '6px'
              }}>
                {searchResults.map((result, idx) => (
                  <li
                    key={idx}
                    onMouseDown={() => handlePlaceSelect(result)}
                    style={{
                      padding: '10px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee'
                    }}
                  >
                    {result.name} <span style={{ color: '#888' }}>({result.lat}, {result.lng})</span>
                  </li>
                ))}
              </ul>
            )}

            


          </div>
        </div>
      )}


    </div>
  )
};





export default Map;
